import { memo, useCallback, useMemo, useState } from 'react';
import { CircularProgress, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { Accordion } from '@common/components/Accordion';
import { StyledTooltip } from '@/modules/common/components/StyledTooltip';
import { ConfirmDialog } from '@/modules/common/components/ConfirmDialog';
import { DeleteIcon, DownloadIcon } from '@/assets/icons';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { FileUploadButtonWithSubtext } from '@/modules/common/components/FileUploadButtonWithSubtext';
import { OverflowTypography } from '@/modules/common/components/OverflowTypography';
import { FileServiceConnector } from '@/modules/api/FileApiConnector';
import { POINT_CLOUD_FILES_CONTAINER, PROJECT_FILES_ROOT_CONTAINER } from '@/modules/api/constant';
import { buildRevisionFileStorageBasePath } from '@/modules/common/helpers/buildRevisionFileStorageBasePath';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { logger } from '@/modules/common/logging/logger';
import { downloadFile } from '@/modules/common/helpers/browser';
import { FileApiConnector } from '@/modules/api/FileApiConnector/FileApiConnector';

import { usePointCloudUIStore } from '../store';
import { IsLatestRevisionSelector } from '@/selectors';

const PropertiesComponent = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbarStore();
  const floorplanInteractionMode = useAppStore((state) => state.mode);
  const [deletePointCloudOpened, setDeletePointCloudOpened] = useState(false);
  const [deleteInputFilesOpened, setDeleteInputFilesOpened] = useState(false);
  const isLatest = IsLatestRevisionSelector.useReactive();
  
  const { inputFile, outputFile } = usePointCloudUIStore(
    useShallow((state) => ({
      inputFile: state.inputFile,
      outputFile: state.outputFile,
    })),
  );

  const pointCloudInputFileRef = useFloorplanDesignStore(
    (state) => state.uxonly.pointCloudInputFileRef,
  );
  const inputFileDisplayNameParts = pointCloudInputFileRef
    ? pointCloudInputFileRef.split('/')
    : undefined;
  inputFileDisplayNameParts?.shift();
  const inputFileDisplayName = inputFileDisplayNameParts?.join('/');

  const pointCloudOutputFileRef = useFloorplanDesignStore(
    (state) => state.uxonly.pointCloudOutputFileRef,
  );
  const outputFileDisplayNameParts = pointCloudOutputFileRef
    ? pointCloudOutputFileRef.split('/')
    : undefined;
  outputFileDisplayNameParts?.shift();
  const outputFileDisplayName = outputFileDisplayNameParts?.join('/');

  const onUploadRecordings = useCallback(
    async (file: File) => {
      const { setUploadProcess } = usePointCloudUIStore.getState().inputFileActions;
      setUploadProcess({ status: 'loading' });

      const isValidType = ['application/x-zip-compressed', 'application/zip', 'zip'].includes(
        file.type,
      );

      // validate
      if (!isValidType) {
        setUploadProcess({ status: 'error', code: 'incompatible_file' });
        showSnackbar(t('errors:reference.incompatible_file'));
        return;
      }

      const fileRef = `${POINT_CLOUD_FILES_CONTAINER}/${file.name}`;

      // upload to file api
      const basePath = buildRevisionFileStorageBasePath();

      if (!basePath) {
        logger.error('basePath not defined.');
        setUploadProcess({ status: 'error', code: 'save_failed' });
        showSnackbar(t('errors:reference.failed_to_save'));
        return;
      }

      await FileServiceConnector.getInstance().addFile(
        `${basePath}/${fileRef}`,
        file,
        'zip',
        PROJECT_FILES_ROOT_CONTAINER,
      );

      // store reference in fddm
      FddmServiceFactory.getInstance()
        .createFddmService()
        .addPointCloudInputFileRef(fileRef);
    },
    [showSnackbar, t],
  );

  const onUploadPointCloud = useCallback(
    async (file: File): Promise<void> => {
      const { setUploadProcess } = usePointCloudUIStore.getState().outputFileActions;
      setUploadProcess({ status: 'loading' });

      const isValidType = ['ply'].includes(file.type) || file.type === '';

      // validate
      if (!isValidType) {
        setUploadProcess({ status: 'error', code: 'incompatible_file' });
        showSnackbar(t('errors:reference.incompatible_file'));
        return;
      }

      const fileRef = `${POINT_CLOUD_FILES_CONTAINER}/${file.name}`;

      // upload to file api
      const basePath = buildRevisionFileStorageBasePath();

      if (!basePath) {
        logger.error('basePath not defined.');
        setUploadProcess({ status: 'error', code: 'save_failed' });
        showSnackbar(t('errors:reference.failed_to_save'));
        return;
      }

      await FileServiceConnector.getInstance().addFile(
        `${basePath}/${fileRef}`,
        file,
        'ply',
        PROJECT_FILES_ROOT_CONTAINER,
      );

      // store reference in fddm
      FddmServiceFactory.getInstance()
        .createFddmService()
        .addPointCloudOutputFileRef(fileRef);
    },
    [showSnackbar, t],
  );

  const onDeleteInputFilesBtnClick = useCallback(() => {
    setDeleteInputFilesOpened(true);
  }, []);

  const onDeletePointCloudBtnClick = useCallback(() => {
    setDeletePointCloudOpened(true);
  }, []);

  const onDownloadInputFilesBtnClick = useCallback(async () => {
    const fileRef = useFloorplanDesignStore.getState().uxonly.pointCloudInputFileRef;

    if (!fileRef) {
      logger.error(
        'Could not proceed with downloading the point cloud input file. FileRef not defined.',
      );
      return;
    }

    const basePath = buildRevisionFileStorageBasePath();
    const result = await FileApiConnector.getInstance().getBlobFileByCategory(
      `${basePath}/${fileRef}`,
      PROJECT_FILES_ROOT_CONTAINER,
    );

    const fileNameParts = fileRef.split('/');
    fileNameParts.shift(); // remove category "<category>/filename.extension"

    downloadFile(result, result.type, fileNameParts.join());
  }, []);

  const onDownloadPointCloudBtnClick = useCallback(async () => {
    const fileRef = useFloorplanDesignStore.getState().uxonly.pointCloudOutputFileRef;

    if (!fileRef) {
      logger.error(
        'Could not proceed with downloading the point cloud output file. FileRef not defined.',
      );
      return;
    }

    const basePath = buildRevisionFileStorageBasePath();
    const result = await FileApiConnector.getInstance().getBlobFileByCategory(
      `${basePath}/${fileRef}`,
      PROJECT_FILES_ROOT_CONTAINER,
    );

    const fileNameParts = fileRef.split('/');
    fileNameParts.shift(); // remove category "<category>/filename.extension"

    downloadFile(result, result.type, fileNameParts.join());
  }, []);

  const closeDeleteInputFilesConfirmDialog = useCallback(() => {
    setDeleteInputFilesOpened(false);
  }, []);

  const closeDeletePointCloudConfirmDialog = useCallback(() => {
    setDeletePointCloudOpened(false);
  }, []);

  const onDeleteInputFilesConfirmClick = useCallback(() => {
    FddmServiceFactory.getInstance().createFddmService().deletePointCloudInputFileRef();
    closeDeleteInputFilesConfirmDialog();

    const { inputFileActions } = usePointCloudUIStore.getState();
    inputFileActions.setUploadProcess({ status: 'idle' });
  }, [closeDeleteInputFilesConfirmDialog]);

  const onDeletePointCloudConfirmClick = useCallback(() => {
    FddmServiceFactory.getInstance().createFddmService().deletePointCloudOutputFileRef();
    closeDeletePointCloudConfirmDialog();

    const { outputFileActions } = usePointCloudUIStore.getState();
    outputFileActions.setUploadProcess({ status: 'idle' });
  }, [closeDeletePointCloudConfirmDialog]);

  const globalDisabled = floorplanInteractionMode !== 'SELECT_ONLY' || !isLatest;

  const inputControlsDisabled = useMemo(
    () => globalDisabled || inputFile.uploadProcess.status === 'loading',
    [globalDisabled, inputFile.uploadProcess.status],
  );

  const outputControlsDisabled = useMemo(
    () => globalDisabled || outputFile.uploadProcess.status === 'loading',
    [globalDisabled, outputFile.uploadProcess.status],
  );

  const showInputUploadButton =
    inputFile.uploadProcess.status === 'idle' || inputFile.uploadProcess.status === 'error';

  const showOutputUploadButton =
    outputFile.uploadProcess.status === 'idle' || outputFile.uploadProcess.status === 'error';

  return (
    <>
      {deleteInputFilesOpened && (
        <ConfirmDialog
          title={t('interface:setup.pointcloud.video_files.remove.title')}
          content={t('interface:setup.pointcloud.video_files.remove.description')}
          yesLabel={t('common:confirm')}
          noLabel={t('common:cancel')}
          open
          onClose={closeDeleteInputFilesConfirmDialog}
          onConfirm={onDeleteInputFilesConfirmClick}
          onDecline={closeDeleteInputFilesConfirmDialog}
        />
      )}

      {deletePointCloudOpened && (
        <ConfirmDialog
          title={t('interface:setup.pointcloud.pointcloud_file.remove.title')}
          content={t('interface:setup.pointcloud.pointcloud_file.remove.description')}
          yesLabel={t('common:confirm')}
          noLabel={t('common:cancel')}
          open
          onClose={closeDeletePointCloudConfirmDialog}
          onConfirm={onDeletePointCloudConfirmClick}
          onDecline={closeDeletePointCloudConfirmDialog}
        />
      )}

      <Accordion title={t('interface:setup.pointcloud.properties_title')} sx={{ px: 2 }}>
        <Stack gap={1} data-testid='PointCloudProperties'>
          <Stack
            direction='row'
            // gap={1}
            justifyContent='space-between'
            alignItems='center'
            width='100%'
          >
            <Typography sx={{ fontSize: '12px' }}>
              {t('interface:setup.pointcloud.video_files.properties_title')}
            </Typography>

            {pointCloudInputFileRef && (
              <>
                <StyledTooltip title={t('interface:setup.pointcloud.video_files.remove.title')}>
                  <IconButton
                    onMouseDown={onDownloadInputFilesBtnClick}
                    sx={{
                      p: 0,
                      width: '24px',
                      height: '24px',
                      marginLeft: 'auto',
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </StyledTooltip>

                <StyledTooltip title={t('interface:setup.pointcloud.video_files.remove.title')}>
                  <IconButton
                    onMouseDown={onDeleteInputFilesBtnClick}
                    sx={{ p: 0, width: '24px', height: '24px' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </StyledTooltip>
              </>
            )}
          </Stack>

          {inputFile.uploadProcess.status === 'loading' && <CircularProgress size={18} />}

          {inputFileDisplayName && (
            <OverflowTypography
              sx={{
                display: 'flex',
                fontSize: 12,
                width: '100%',
              }}
            >
              {inputFileDisplayName}
            </OverflowTypography>
          )}

          {showInputUploadButton && (
            <FileUploadButtonWithSubtext
              disabled={inputControlsDisabled}
              onUploaded={onUploadRecordings}
              label={t('interface:setup.pointcloud.video_files.upload.button.text')}
              accept='.zip'
              subText='.zip'
            />
          )}

          <Divider />

          <Stack direction='row' justifyContent='space-between' alignItems='center' width='100%'>
            <Typography sx={{ fontSize: '12px' }}>
              {t('interface:setup.pointcloud.pointcloud_file.properties_title')}
            </Typography>

            {pointCloudOutputFileRef && (
              <>
                <StyledTooltip title={t('interface:setup.pointcloud.pointcloud_file.remove.title')}>
                  <IconButton
                    onMouseDown={onDownloadPointCloudBtnClick}
                    sx={{
                      p: 0,
                      width: '24px',
                      height: '24px',
                      marginLeft: 'auto',
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </StyledTooltip>

                <StyledTooltip title={t('interface:setup.pointcloud.pointcloud_file.remove.title')}>
                  <IconButton
                    onMouseDown={onDeletePointCloudBtnClick}
                    sx={{ p: 0, width: '24px', height: '24px' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </StyledTooltip>
              </>
            )}
          </Stack>

          {outputFile.uploadProcess.status === 'loading' && <CircularProgress size={18} />}

          {outputFileDisplayName && (
            <OverflowTypography
              sx={{
                display: 'flex',
                fontSize: 12,
              }}
            >
              {outputFileDisplayName}
            </OverflowTypography>
          )}

          {showOutputUploadButton && (
            <FileUploadButtonWithSubtext
              disabled={outputControlsDisabled}
              onUploaded={onUploadPointCloud}
              label={t('interface:setup.pointcloud.pointcloud_file.upload.button.text')}
              accept='.ply'
              subText='.ply'
            />
          )}
        </Stack>
      </Accordion>
    </>
  );
};

export const Properties = memo(PropertiesComponent);
